// Notification SOUNDS — one Android channel per sound.
//
// ⚠️ ON ANDROID 8+ THE CHANNEL OWNS THE SOUND. The server's `sound` field is
// ignored there; what picks the sound is `android.notification.channelId`, and
// the backend (Home Automation api/new/notify.js, and the older senders in
// api/index.js / asynccontrollers.js) sends `channel_<sound>`. So every sound the
// server may name needs (1) a file in android/app/src/main/res/raw and (2) a
// channel here with the SAME name. A channelId the phone has no channel for
// falls back to FCM's "Miscellaneous" channel and the system sound — silently.
//
// ⚠️ A CHANNEL'S SOUND CAN NEVER BE CHANGED after it is created (Android keeps
// the first one forever). To change a sound, add a NEW sound name — never edit
// an existing entry's file. Adding a sound needs a new APK (the file ships in
// the APK); OTA cannot deliver it.
//
// The files are the SmartPagar set (same company, same ids), copied 2026-09-29.
// The backend already uses sound4 / sound5 / sound6 for location / room / device
// lock alerts.

export const DEFAULT_SOUND = 'default';
// A notification that arrives WITHOUT a sound (owner, 2026-10-10: "no sound on
// motion"). Its channel is LOW importance — shown in the shade, never rung,
// never a heads-up banner. It needs no res/raw file, so the channel is created
// by this JS and reaches phones by OTA; a phone still on an older bundle has no
// channel_silent and falls back to the system sound until it updates.
export const SILENT_SOUND = 'silent';

export const SOUNDS = [
  { sound: 'default', name: 'General' },   // system notification sound
  { sound: 'silent', name: 'Silent (no sound)' },
  { sound: 'sound1', name: 'Alert 1' },
  // ⚠️ HIDDEN: sound2.wav is byte-for-byte sound1.wav (Alert 1). It stays —
  // the motion sensor's default names it (rfTypes + the backend's TYPES), and a
  // created channel cannot be removed from a phone — but no picker offers it.
  { sound: 'sound2', name: 'Alert 2 (same as Alert 1)', hidden: true },
  { sound: 'sound3', name: 'Alert 3' },
  { sound: 'sound4', name: 'Location lock alert' },
  { sound: 'sound5', name: 'Room lock alert' },
  { sound: 'sound6', name: 'Device lock alert' },
  { sound: 'telephone_ring', name: 'Telephone ring' },
  { sound: 'main_door_opened', name: 'Door opened' },
  { sound: 'main_door_closed', name: 'Door closed' },
  { sound: 'smartify', name: 'Smartify' },
  { sound: 'smartify_male', name: 'Smartify (male)' },
  { sound: 'smartclick', name: 'SmartClick' },
  { sound: 'smartsecure', name: 'SmartSecure' },
  { sound: 'smartppagar', name: 'SmartPpagar' },
  { sound: 'sharpaccounting', name: 'SharpAccounting' },
  // ── Alarms & warnings (2026-10-06) ─────────────────────────────────────
  // Synthesised by scripts/gen-alarm-sounds.js — ours, no licence. ⚠️ NEW
  // APK REQUIRED: a phone on an older build has no channel_<name> for these and
  // falls back to the system sound, so ship the APK before choosing them for a
  // customer's sensor. Never re-tune one under the same name (see the header).
  { sound: 'alarm_siren', name: 'Alarm — Siren' },
  { sound: 'alarm_yelp', name: 'Alarm — Fast siren (yelp)' },
  { sound: 'alarm_hilo', name: 'Alarm — Hi-lo (nee-naw)' },
  { sound: 'alarm_beep_fast', name: 'Alarm — Smoke alarm beeps' },
  { sound: 'alarm_klaxon', name: 'Alarm — Klaxon' },
  { sound: 'alarm_panic', name: 'Alarm — Panic' },
  { sound: 'warning_chime', name: 'Warning — Chime' },
  { sound: 'warning_pulse', name: 'Warning — Pulse' },
  // ── Short & loud, under a second — door opened (same script, same rules) ─
  { sound: 'beep_single', name: 'Beep — Single (short)' },
  { sound: 'beep_double', name: 'Beep — Double (short)' },
  { sound: 'beep_triple', name: 'Beep — Triple (short)' },
  { sound: 'beep_long', name: 'Beep — Long (0.6 s)' },
  { sound: 'chirp_up', name: 'Chirp — Rising (opened)' },
  { sound: 'chirp_down', name: 'Chirp — Falling (closed)' },
  { sound: 'buzz_short', name: 'Buzz — Short' },
  { sound: 'ding_dong', name: 'Ding-dong (short)' },
  // ── SPOKEN (2026-10-21, the owner) ───────────────────────────────
  // Words, not a beep: the door-left-open REMINDER says what it wants, and the
  // confirmation says it is done (api/new/reminders.js). Synthesised from the
  // OS's own voice by scripts/gen-voice-sounds.js — ours, no licence.
  // ⚠️ NEW APK REQUIRED, like every sound here: the .wav ships inside it and a
  // phone on an older build has no channel_voice_* — its reminder then falls
  // back to the system sound, silently. Never re-record one under the same name.
  { sound: 'voice_close_door', name: 'Voice — “Please close room door”' },
  { sound: 'voice_door_closed', name: 'Voice — “Room door closed”' },
];

/**
 * What a sound PICKER offers: every sound not hidden — plus `current` when it is
 * a hidden one, so a sensor already set to it still shows its value instead of
 * a blank select. Channels are still created for ALL of SOUNDS.
 */
export function soundOptions(current) {
  return SOUNDS.filter((s) => !s.hidden || s.sound === current);
}

export const channelIdFor = (sound) => `channel_${sound || DEFAULT_SOUND}`;

/**
 * Create every channel. Idempotent — Android ignores a create for an existing
 * id (which is also why a sound cannot be changed afterwards). One failure does
 * not stop the rest.
 */
export async function createSoundChannels(PushNotifications) {
  for (const s of SOUNDS) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await PushNotifications.createChannel({
        id: channelIdFor(s.sound),
        name: s.name,
        description: s.sound === DEFAULT_SOUND ? 'Home alerts' : `Home alerts — ${s.name}`,
        // HIGH: heads-up banner + sound. Silent is LOW: in the shade, no sound.
        importance: s.sound === SILENT_SOUND ? 2 : 5,
        visibility: 1,     // shown on the lock screen
        vibration: s.sound !== SILENT_SOUND,
        lights: s.sound !== SILENT_SOUND,
        ...(s.sound === DEFAULT_SOUND || s.sound === SILENT_SOUND ? {} : { sound: s.sound }),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[push] createChannel failed for', s.sound, e && e.message);
    }
  }
}
